// Resolución de códigos DANE (8 dígitos) para ciudades de Colombia.
// Índice en memoria construido desde src/assets/data/ciudades.json
// (fuente: Divipola del DANE — nombre, abreviado, dpto, codigo_dane, activo).
import citiesJson from '@/assets/data/ciudades.json';

const CITIES = (citiesJson || []).filter((c) => c.activo !== false);

// Ciudades de origen: si el destino coincide con una de estas, no se permite
// cotizar (el envío saldría y llegaría en la misma ciudad).
export const RESTRICTED_CITY_CODES = ['54001000', '54405000', '54874000'];

// Mapeo de province_code (ISO 3166-2, sin prefijo CO-) a nombre de departamento
// tal como aparece en ciudades.json ya normalizado (sin tildes, mayúsculas,
// sin puntos: normalizeName convierte "D.C." en "D C").
export const DEPARTMENT_BY_ISO = {
  AM: 'AMAZONAS',
  ANT: 'ANTIOQUIA',
  ARA: 'ARAUCA',
  ATL: 'ATLANTICO',
  BOG: 'BOGOTA D C',
  DC: 'BOGOTA D C',
  BOL: 'BOLIVAR',
  BOY: 'BOYACA',
  CAL: 'CALDAS',
  CAQ: 'CAQUETA',
  CAS: 'CASANARE',
  CAU: 'CAUCA',
  CES: 'CESAR',
  CHO: 'CHOCO',
  COR: 'CORDOBA',
  CUN: 'CUNDINAMARCA',
  GUA: 'GUAINIA',
  GUV: 'GUAVIARE',
  HUI: 'HUILA',
  MAG: 'MAGDALENA',
  MET: 'META',
  NAR: 'NARINO',
  NSA: 'NORTE DE SANTANDER',
  PUT: 'PUTUMAYO',
  QUI: 'QUINDIO',
  RIS: 'RISARALDA',
  SAN: 'SANTANDER',
  SAP: 'ARCHIPIELAGO DE SAN ANDRES PROVIDENCIA Y SANTA CATALINA',
  SUC: 'SUCRE',
  TOL: 'TOLIMA',
  VAC: 'VALLE DEL CAUCA',
  VAU: 'VAUPES',
  VID: 'VICHADA',
  LAG: 'LA GUAJIRA',
};

// Nombres alternativos comunes con los que Shopify u otros sistemas envían
// la ciudad, mapeados al nombre normalizado del dataset.
const ALIASES = {
  CUCUTA: 'SAN JOSE DE CUCUTA',
  BOGOTA: 'BOGOTA D.C.',
  CALI: 'SANTIAGO DE CALI',
  CARTAGENA: 'CARTAGENA',
  'BOGOTA DC': 'BOGOTA D.C.',
  'BOGOTA D C': 'BOGOTA D.C.',
};

// Normaliza: NFD → quitar diacríticos → mayúsculas → colapsar espacios.
export function normalizeName(value) {
  return (value || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[,.\-()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Precomputa los nombres normalizados de ciudades y departamentos una sola vez.
const INDEX = CITIES.map((c) => ({
  raw: c,
  nombre: normalizeName(c.nombre),
  abreviado: normalizeName(c.abreviado),
  dpto: normalizeName(c.dpto),
}));

/**
 * Resuelve el código DANE de 8 dígitos a partir de los datos de dirección de envío.
 *
 * @param {Object|string} city Nombre de la ciudad (o dirección con city/province/zip).
 * @param {string} [province] Nombre de provincia/departamento.
 * @param {string} [provinceCode] Código ISO (ej. DC, ANT, NSA).
 * @param {string} [zip] Fallback: código postal.
 * @returns {string} Código DANE de 8 dígitos, o '' si no se pudo resolver.
 */
export function resolveCityDane(city, province = '', provinceCode = '', zip = '') {
  const cityName = typeof city === 'object' ? (city?.city || '') : (city || '');
  const provName = typeof city === 'object' ? (city?.province || '') : province;
  const provCode = typeof city === 'object' ? (city?.province_code || '') : provinceCode;
  const zipCode = typeof city === 'object' ? (city?.zip || '') : zip;

  const query = normalizeName(cityName);
  if (!query) return '';

  const candidates = searchCandidates(query);

  if (candidates.length === 0) {
    // Fallback por código postal: algunos zips coinciden con el prefijo DANE
    const zipQuery = normalizeName(zipCode);
    if (zipQuery) {
      const byZip = INDEX.filter((c) => c.raw.codigo_dane.startsWith(zipQuery.slice(0, 2)));
      if (byZip.length === 1) return byZip[0].raw.codigo_dane;
    }
    return '';
  }

  // Desambiguar por departamento en todas las capas de búsqueda. Si el
  // departamento es conocido y ningún candidato le pertenece, el nombre no
  // corresponde a una ciudad resolvible y devolver uno arbitrario crearía la
  // guía con un destino equivocado: preferimos no resolver.
  const expected = departmentFrom(provName, provCode);
  const best = sortCandidates(candidates, expected)[0];

  if (expected && departmentScore(best, expected) === 0) {
    return '';
  }

  return best.raw.codigo_dane;
}

/**
 * Indica si un código DANE pertenece a una de las ciudades restringidas
 * (misma ciudad de origen del envío).
 */
export function isRestrictedCity(daneCode) {
  return RESTRICTED_CITY_CODES.includes(daneCode);
}

/**
 * Nombre del departamento normalizado a partir de la provincia o su código ISO.
 */
function departmentFrom(provinceName, provinceCode) {
  const byName = normalizeName(provinceName);

  if (byName) {
    // Comparar ignorando espacios internos ("BOGOTA DC" = "BOGOTA D.C.")
    const clean = (s) => s.replace(/\s/g, '');
    const exact = Object.values(DEPARTMENT_BY_ISO).find((d) => clean(d) === clean(byName));
    if (exact) return exact;

    const aliased = ALIASES[byName];
    if (aliased) return normalizeName(aliased);
  }

  const iso = normalizeName(provinceCode).replace(/^CO-/, '');
  if (iso && DEPARTMENT_BY_ISO[iso]) {
    return DEPARTMENT_BY_ISO[iso];
  }

  return '';
}

/**
 * Búsqueda por capas: exacta → prefijo (ambos sentidos) → primera palabra.
 */
function searchCandidates(query) {
  const aliases = [query, ALIASES[query]].filter(Boolean);

  for (const q of aliases) {
    // 1. Coincidencia exacta por nombre o abreviado
    const exact = INDEX.filter((c) => c.nombre === q || c.abreviado === q);
    if (exact.length > 0) return exact;
  }

  for (const q of aliases) {
    // 2. Por prefijo, en ambos sentidos
    const byPrefix = INDEX.filter(
      (c) => c.nombre.startsWith(q) || q.startsWith(c.nombre)
        || c.abreviado.startsWith(q) || q.startsWith(c.abreviado)
    );
    if (byPrefix.length > 0) return byPrefix;
  }

  for (const q of aliases) {
    // 3. Por primera palabra
    const firstWord = q.split(' ')[0];
    const byFirstWord = INDEX.filter(
      (c) => c.nombre.split(' ')[0] === firstWord || c.abreviado.split(' ')[0] === firstWord
    );
    if (byFirstWord.length > 0) return byFirstWord;
  }

  return [];
}

/**
 * Departamentos que las tiendas informan indistintamente por su entidad
 * territorial ("Bogotá D.C." también llega como "Cundinamarca").
 */
const DEPARTMENT_EQUIVALENTS = [
  ['BOGOTA D C', 'CUNDINAMARCA'],
];

/**
 * Puntaje de cercanía entre un candidato y el departamento esperado:
 * igualdad = 3, equivalencia/prefijo = 2, palabra completa = 1; 0 si no se relacionan.
 */
function departmentScore(candidate, expectedDpto) {
  if (!expectedDpto) return 0;
  const dpto = candidate.dpto;
  if (dpto === expectedDpto) return 3;
  const equivalent = DEPARTMENT_EQUIVALENTS.some(
    ([a, b]) => (dpto === a && expectedDpto === b) || (dpto === b && expectedDpto === a)
  );
  if (equivalent) return 2;
  if (dpto.startsWith(expectedDpto) || expectedDpto.startsWith(dpto)) return 2;
  const words = expectedDpto.split(' ');
  if (words.some((w) => w.length > 2 && dpto.split(' ').includes(w))) return 1;
  return 0;
}

/**
 * Ordena candidatos por cercanía del departamento esperado; el resto queda al final.
 */
function sortCandidates(candidates, expectedDpto) {
  return [...candidates].sort((a, b) => {
    const diff = departmentScore(b, expectedDpto) - departmentScore(a, expectedDpto);
    if (diff !== 0) return diff;
    return a.raw.codigo_dane.localeCompare(b.raw.codigo_dane);
  });
}

const byName = (a, b) => a.localeCompare(b, 'es');

/**
 * Departamentos del dataset con su nombre oficial, ordenados alfabéticamente.
 * Es la lista que usan todos los formularios (remisiones, catálogo, envíos).
 */
export const DEPARTMENTS = Array.from(new Set(CITIES.map((c) => c.dpto))).sort(byName);

/**
 * Nombres oficiales de las ciudades de un departamento, ordenados.
 */
export function citiesOfDepartment(dpto) {
  return CITIES.filter((c) => c.dpto === dpto).map((c) => c.nombre).sort(byName);
}

/**
 * Convierte un departamento/ciudad guardado con otro formato (p. ej. "ANTIOQUIA"
 * o "Cúcuta", del listado anterior) a los nombres oficiales del dataset.
 * Devuelve '' en el campo que no se pueda reconocer.
 */
const DEPARTMENT_ALIASES = {
  'SAN ANDRES Y PROVIDENCIA': 'ARCHIPIELAGO DE SAN ANDRES PROVIDENCIA Y SANTA CATALINA',
};

export function canonicalLocation(departamento, municipio) {
  const clean = (s) => normalizeName(s).replace(/\s/g, '');
  const dptoQuery = clean(DEPARTMENT_ALIASES[normalizeName(departamento)] || departamento);
  const fromIso = clean(departmentFrom(departamento, ''));
  const dptoMatch = dptoQuery
    ? INDEX.find((c) => c.dpto.replace(/\s/g, '') === dptoQuery || c.dpto.replace(/\s/g, '') === fromIso)
    : null;
  const dpto = dptoMatch ? dptoMatch.raw.dpto : '';

  const city = matchCityName(municipio, dpto);
  if (!city) {
    return { departamento: dpto, municipio: '' };
  }
  return { departamento: city.dpto, municipio: city.nombre };
}

/**
 * Búsqueda estricta de una ciudad por nombre (sin prefijos parciales, que darían
 * falsos positivos como "Sincé" → "Sincelejo"): exacta/alias → las palabras de la
 * consulta contenidas en el nombre oficial. Solo devuelve un resultado si es único.
 */
function matchCityName(municipio, dpto) {
  const query = normalizeName(municipio);
  if (!query) return null;
  const dptoNorm = dpto ? normalizeName(dpto) : '';
  const pool = dptoNorm ? INDEX.filter((c) => c.dpto === dptoNorm) : INDEX;

  const targets = [query, normalizeName(ALIASES[query])].filter(Boolean);
  const exact = pool.filter((c) => targets.includes(c.nombre) || targets.includes(c.abreviado));
  if (exact.length === 1) return exact[0].raw;

  const words = query.split(' ');
  const byWords = pool.filter((c) => {
    const nameWords = c.nombre.split(' ');
    return words.every((w) => nameWords.includes(w));
  });
  return byWords.length === 1 ? byWords[0].raw : null;
}
